import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Bell, BellRing, Loader2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { getExistingSubscription, subscribeToPush, pushSupported } from "@/lib/push";

/** Watch a coin → browser push alerts (anonymous OK) + email alerts when signed in. */
export default function WatchButton({ coin }) {
  const { user } = useAuth();
  const [watching, setWatching] = useState(false);
  const [count, setCount] = useState(0);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      const sub = await getExistingSubscription().catch(() => null);
      const q = sub?.endpoint ? `?endpoint=${encodeURIComponent(sub.endpoint)}` : "";
      api.get(`/coins/${coin.slug}/watchers${q}`).then((r) => {
        if (!alive) return;
        setCount(r.data.count || 0);
        setWatching(!!r.data.watching);
      }).catch(() => {});
    })();
    return () => { alive = false; };
  }, [coin.slug, user?.user_id]);

  const toggle = async () => {
    setBusy(true);
    try {
      if (watching) {
        const sub = await getExistingSubscription().catch(() => null);
        const q = sub?.endpoint ? `?endpoint=${encodeURIComponent(sub.endpoint)}` : "";
        const r = await api.delete(`/watch/${coin.slug}${q}`);
        setWatching(false);
        setCount(r.data.count);
        toast("Alerts off for $" + coin.ticker);
        return;
      }
      const subscription = await subscribeToPush().catch(() => null);
      if (!subscription && !user) {
        toast.error(pushSupported() ? "Allow notifications (or sign in) to watch this coin" : "Sign in to watch this coin — this browser can't receive push alerts");
        return;
      }
      const r = await api.post(`/watch/${coin.slug}`, { subscription, email_alerts: !!user });
      setWatching(true);
      setCount(r.data.count);
      toast.success(
        subscription
          ? `Watching $${coin.ticker} — you'll get alerts for tips, mint and pool launches`
          : `Watching $${coin.ticker}${r.data.email_alerts ? " — email alerts on" : ""}`,
        { action: { label: "Open watchlist", onClick: () => { window.location.href = "/watchlist"; } } }
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't update watch");
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      onClick={toggle}
      disabled={busy}
      data-testid="watch-button"
      data-watching={watching ? "1" : "0"}
      className={`border px-4 py-2.5 font-mono text-xs uppercase tracking-widest flex items-center gap-2 transition-colors ${
        watching ? "border-[#cfff04] text-[#cfff04] bg-[#cfff04]/10" : "border-[#3a3a3a] text-[#e2e2e2] hover:border-[#cfff04] hover:text-[#cfff04]"
      }`}
    >
      {busy ? <Loader2 size={12} className="animate-spin" /> : watching ? <BellRing size={12} /> : <Bell size={12} />}
      {watching ? "Watching" : "Watch"} <span data-testid="watch-count">{count}</span>
    </button>
  );
}
